import { warn as logWarn } from './logger';
import { sanitizePlacementId } from './recommendationsUtils';
import Cookies from '../services/cookies';
import {
    AB_TEST_PDP_RIGHT_RAIL,
    AB_TEST_PDP_ALSO_BOUGHTWITH_TRAY,
    AB_REC3
} from '../abTestingConstants';
import { isDeviceTypeLargerThan, DEVICE_CHARACTERISTICS_SIZE } from '../../../core/js/utils/generalUtils';
import { SLIDES_PER_VIEW_BOUGHT_WITH } from '../../../core/js/constants';
import { getPrimaryProductRecommendationPlacement } from '../../../product/js/utils/utils';

/**
 * Determine the RR placement to use.
 * If specified, just return it.
 * If there's only one placement available, return that placement,
 * Otherwise log an error that the placement could not be determined and return null
 *
 * @param  {Object} state The Redux state
 * @param  {String} rrPlacement The placement (can be undefined)
 * @returns {String} The placement if it can be determined, otherwise null
 */
export function getRRPlacement(state, rrPlacement) {
    const allRRRecs = _.get(state, 'core.rrRecs', {});
    let placement = rrPlacement;

    if (!placement) {
        if (_.size(_.keys(allRRRecs)) === 1) {
            placement = _.head(_.keys(allRRRecs));
        } else {
            logWarn('no RR placement argument given to getRRPlacement and there\'s more than one placement available');
            return null;
        }
    } else {
        // placement might be the placement ID from RR, we want the return the placement key in core.rrRecs
        placement = sanitizePlacementId(placement);
    }

    return placement;
}

/**
 * Finds Rich Relevance container, checks for and gets rrPlacement data attribute
 * @returns {String} the placement
 */
export function getRichRelevancePlacementData() {
    const $rrContainer = $('.dom-rr-container');
    const placementData = {};
    const dyPlacementIds = [];
    let dyPlacementTileCount = {};

    if ($rrContainer.length > 0) {

        const placements = _.reduce($rrContainer, (acc, elem) => {
            const placementValue = $(elem).attr('data-rr-placement');
            const tileCount = $(elem).attr('data-tile-count');
            const isDyRecommendation = $(elem).attr('data-is-dy');

            if (isDyRecommendation) {
                dyPlacementIds.push(placementValue);
                if (tileCount) {
                    dyPlacementTileCount = { ...dyPlacementTileCount, [placementValue]: tileCount };
                }
            } else if (placementValue && _.trim(placementValue) !== '') {
                acc.push(placementValue);
            }

            return acc;

        }, []);

        if (placements.length > 0) {
            placementData.rrPlacementId = _.join(placements, ',');
        }

        if (dyPlacementIds.length > 0) {
            placementData.dyPlacementIds = dyPlacementIds;
            placementData.dyPlacementTileCount = dyPlacementTileCount;
        }
    }

    return placementData;
}

/**
 * Get the product records from an RR tray
 * @param {object} state Redux state object
 * @param {String} rrPlacement the RR placement id to use (can be undefined, if there's only one)
 * @returns {array} array of product records to feed into a quickshop product generator
 */
export function getRRProductRecords(state, rrPlacement) {
    const placement = getRRPlacement(state, rrPlacement);

    if (!placement) {
        return {};
    }

    const products = _.get(state, `core.rrRecs.${placement}.recs`, []);

    return _.map(products, (product) => product.product);
}

/**
 * Get the comma seperated richRelevance placementId String for PDP
 * @param {object} state Redux state object
 * @returns {string} placementId string to be used for RichRelevance Request
 */
export function getPlacementIdForPDP(state) {
    const primaryProductPlacement = getPrimaryProductRecommendationPlacement(state);
    const alsoBoughtWithTrayEnabled = isAlsoBoughtTrayEnabled(state);
    const alsoBoughtWithPlacement = getDYPlacementIdFromSettings(state, 'productAlsoBoughtWith');
    const visuallySimilarPlacement = getDYPlacementIdFromSettings(state, 'productVisuallySimilar');

    const outOfStockTrayValue = Cookies.getCookieByName(AB_REC3);
    const showOutOfStockTray = outOfStockTrayValue !== '0';

    let placementId = primaryProductPlacement;

    if (alsoBoughtWithTrayEnabled && alsoBoughtWithPlacement) {
        placementId += `,${alsoBoughtWithPlacement}`;
    }

    if (_.get(state, 'product.isSoldOut') && showOutOfStockTray) {
        placementId += `,${visuallySimilarPlacement}`;
    }

    return placementId;
}

/**
 * Determine if app should use the Also Bought With Rich Relevance Tray on PDP
 * @param {object} state Redux state object
 * @returns {boolean} flag for using the right rail layout or not
 */

export function isAlsoBoughtTrayEnabled(state) {
    const placementId = getDYPlacementIdFromSettings(state, 'productAlsoBoughtWith');
    const abwIsEnabledInSettings = _.get(state, 'core.constants.thirdParty.default.rich_relevance.also_bought_with_enabled');
    const abwCookieValue = Cookies.getCookieByName(AB_TEST_PDP_ALSO_BOUGHTWITH_TRAY);
    const abwDisabledViaCookie = abwCookieValue === '1';
    const abwEnabledViaCookie = abwCookieValue === '2';

    return !abwDisabledViaCookie && (abwIsEnabledInSettings || abwEnabledViaCookie) && placementId;
}

/**
 * Determine if app should use the right rail layout for PDP rich relevance
 * @param {object} state Redux state object
 * @returns {boolean} flag for using the right rail layout or not
 */
export function isRightRailEnabled(state) {
    const deviceCharacteristics = _.get(state, 'core.deviceCharacteristics', {});
    const isLargeResolution = deviceCharacteristics.is_large || deviceCharacteristics.is_extra_large;
    const rrIsEnabledInSettings = _.get(state, 'core.constants.thirdParty.default.rich_relevance.right_rail_enabled');
    const rrCookieValue = Cookies.getCookieByName(AB_TEST_PDP_RIGHT_RAIL);
    const rrDisabledViaCookie = rrCookieValue && rrCookieValue !== '1';
    const rrEnabledViaCookie = rrCookieValue === '1';

    return !rrDisabledViaCookie && (rrIsEnabledInSettings || rrEnabledViaCookie) && isLargeResolution;
}

/**
 * Determine if right rail is enabled, and rich relevance content exists
 * @param {object} state Redux state object
 * @param {String} rrPlacement the placement id for the right rail container
 * @returns {boolean} flag for using the right rail layout or not
 */
export function isRightRailActive(state, rrPlacement) {
    const rightRailIsEnabled = isRightRailEnabled(state);
    const hasRR = _.get(state, `core.rrRecs.${rrPlacement}.recs`, []).length > 0;

    return rightRailIsEnabled && hasRR;
}

export function getRRPlacementIdFromSettings(state, placementKey) {
    const siteId = _.get(state, 'core.siteId');

    let placementId = _.get(state, `core.constants.thirdParty.${siteId}.rich_relevance.placement_ids.${placementKey}`);

    if (!placementId) {
        placementId = _.get(state, `core.constants.thirdParty.default.rich_relevance.placement_ids.${placementKey}`);
    }

    return placementId;
}

export function getDYPlacementIdFromSettings(state, placementKey) {
    return _.get(state, `core.constants.thirdParty.default.dynamic_yield.placement_ids.${placementKey}`);
}

export function getRRTileTemplate(state, initialStoreLookupPath) {
    let tileType = _.get(state, initialStoreLookupPath);

    if (!tileType) {
        const rrPlacementKey = getRRPlacement(state);

        tileType = 'TITLE_ONLY';

        if (rrPlacementKey) {
            tileType = _.get(state, `core.rrRecs.${rrPlacementKey}.tileTemplate`, 'TITLE_ONLY');
        }
    }

    return tileType;
}

/**
 * Decide how many RR products we should display
 * We need to handle slidesPerView client side so we can properly know which RRrecs recommendation we're working with
 * @param  {object} state  current state
 * @param  {string} placementId name of the current RR placement
 * @return {Number} number of products to display
 */
export function getSlidesPerView(state, placementId) {
    let slidesPerView = _.get(state, 'product.productMeta.rrSlidesPerViewMobile', 2.2);

    if (isDeviceTypeLargerThan(state, DEVICE_CHARACTERISTICS_SIZE.MEDIUM)) {

        if (placementId === getDYPlacementIdFromSettings(state, 'productAlsoBoughtWith')) {
            slidesPerView = SLIDES_PER_VIEW_BOUGHT_WITH;
        } else {
            // This number, rrSlidesPerView, is built in Python, and can't be accurate for client-side RR recs.
            slidesPerView = _.get(state, 'product.productMeta.rrSlidesPerView');
        }
    }

    return slidesPerView;
}

/**
 * Add dy strategy name to a collection of products. Used for tracking the product category (pr1ca) for rectray tiles.
 * product.strategyName is consumed by quickshopSagas.
 * product.product.queryParams is consumed by the macro _displayTile
 * @param  {object} products
 * @param  {string} strategyName Used for GA tracking of the product_category (pr1ca) parameter
 * @return {object} products
 */
export function setStrategyName(products, strategyName) {
    return _.map(products, (p) => {
        if (p.product && strategyName) {
            _.set(p.product, 'strategyName', strategyName);
            _.set(p.product, 'product.queryParams', `recommendation=${strategyName}`);
            return p;
        }
    });
}

export default {
    getRichRelevancePlacementData,
    getRRTileTemplate
};
